/**
 * Cart API Discount Handler
 */

class CartAPIDiscountHandler {
  constructor() {
    this.cart = document.querySelector("cart-drawer");
    this.changeEvent = new Event("change", { bubbles: true });
  }

  /**
   * Applies a discount code to the cart using Cart API
   * @param {string} discountCode - The discount code to be applied
   * @returns {Promise<Object>} Operation result
   */
  async applyDiscountCode(discountCode) {
    try {
      const cartBefore = await this.getCart();

      const response = await fetch(`/cart/update.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          discount: discountCode
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.description || `Error ${response.status}: ${response.statusText}`);
      }

      const cartAfter = await response.json();
      
      if (cartAfter.token) {
        const discountChanged = cartAfter.total_discount !== cartBefore.total_discount;
        const priceChanged = cartAfter.total_price !== cartBefore.total_price;
        const originalPriceChanged = cartAfter.original_total_price !== cartBefore.original_total_price;
        
        const beforeDiscountCount = cartBefore.discount_applications ? cartBefore.discount_applications.length : 0;
        const afterDiscountCount = cartAfter.discount_applications ? cartAfter.discount_applications.length : 0;
        const newDiscountsAdded = afterDiscountCount > beforeDiscountCount;
        
        const newCodeApplied = cartAfter.discount_applications && 
          cartAfter.discount_applications.some(discount => 
            discount.code && discount.code.toLowerCase() === discountCode.toLowerCase()
          );
        
        // The discount is valid if:
        // 1. The new code is actually applied in the discount_applications array, OR
        // 2. There was a positive change in discount/price AND the new code is applied
        if (newCodeApplied || (discountChanged && cartAfter.total_discount > cartBefore.total_discount) || 
            (priceChanged && cartAfter.total_price < cartBefore.total_price)) {
          return {
            success: true,
            cart: cartAfter,
            message: "Discount code applied successfully"
          };
        } else {
          return {
            success: false,
            message: "Provided discount code couldn't be applied to current cart contents."
          };
        }
      } else {
        return {
          success: false,
          message: "Could not apply discount code"
        };
      }

    } catch (error) {
      return {
        success: false,
        message: error.message || "Provided discount code couldn't be applied to current cart contents."
      };
    }
  }

  /**
   * Removes a discount code from the cart
   * @param {string} discountCode - The discount code to be removed
   * @returns {Promise<Object>} Operation result
   */
  async removeDiscountCode(discountCode) {
    try {
      const currentCart = await this.getCart();
      
      if (!currentCart.discount_applications || !Array.isArray(currentCart.discount_applications)) {
        const response = await fetch(`/cart/update.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            discount: ""
          })
        });

        if (!response.ok) {
          throw new Error(`Error ${response.status}: ${response.statusText}`);
        }

        const result = await response.json();
        return {
          success: true,
          cart: result,
          message: "Discount code removed successfully"
        };
      }
      
      const updatedDiscounts = currentCart.discount_applications.filter(
        discount => discount.code.toLowerCase() !== discountCode.toLowerCase()
      );

      if (updatedDiscounts.length === 0) {
        const response = await fetch(`/cart/update.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            discount: ""
          })
        });

        if (!response.ok) {
          throw new Error(`Error ${response.status}: ${response.statusText}`);
        }

        const result = await response.json();
        return {
          success: true,
          cart: result,
          message: "Discount code removed successfully"
        };
      }

      const remainingCodes = updatedDiscounts.map(discount => discount.code);
      return await this.applyDiscountCode(remainingCodes.join(', '));

    } catch (error) {
      return {
        success: false,
        message: error.message || "Error removing discount code"
      };
    }
  }

  /**
   * Gets all currently applied discount codes
   * @returns {Promise<Array>} Array with applied discount codes
   */
  async getAppliedDiscountCodes() {
    try {
      const cart = await this.getCart();
      
      if (!cart.discount_applications || !Array.isArray(cart.discount_applications)) {
        if (cart.total_discount && cart.total_discount > 0) {
          return [];
        }
        
        if (cart.original_total_price && cart.original_total_price > cart.total_price) {
          return [];
        }
        
        return [];
      }
      
      const codes = cart.discount_applications.map(discount => discount.code);
      return codes;
    } catch (error) {
      return [];
    }
  }

  /**
   * Gets the current cart token
   * @returns {Promise<string>} Cart token
   */
  async getCartToken() {
    try {
      const response = await fetch('/cart.js');
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const cartData = await response.json();
      return cartData.token;
    } catch (error) {
      return null;
    }
  }

  /**
   * Gets complete cart data
   * @returns {Promise<Object>} Cart data
   */
  async getCart() {
    try {
      const response = await fetch('/cart.js');
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const cartData = await response.json();
      return cartData;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Updates the cart interface (minicart or cart page)
   * @returns {Promise<void>}
   */
  async refreshCartInterface() {
    try {
      if (window.location.pathname.includes("cart")) {
        await this.refreshCartPage();
      } else {
        await this.refreshMinicart();
      }
    } catch (error) {
      console.error('Error refreshing cart interface:', error);
    }
  }

  /**
   * Updates the cart page
   * @returns {Promise<void>}
   */
  async refreshCartPage() {
    try {
      const cartPage = await fetch('/cart');
      
      if (!cartPage.ok) {
        throw new Error("Network response was not ok");
      }

      const parsedCartPage = await cartPage.text();

      const domParsedCartPage = new DOMParser().parseFromString(
        parsedCartPage,
        "text/html"
      );

      const cartItemsSection = document.querySelector(".shopify-section.cart-items");
      if (cartItemsSection) {
        const newCartItemsSection = domParsedCartPage.querySelector(".shopify-section.cart-items");
        if (newCartItemsSection) {
          cartItemsSection.innerHTML = newCartItemsSection.innerHTML;
        }
      }

      const footerWrapper = document.querySelector(".cart__footer-wrapper");
      if (footerWrapper) {
        const newFooterWrapper = domParsedCartPage.querySelector(".cart__footer-wrapper");
        if (newFooterWrapper) {
          footerWrapper.innerHTML = newFooterWrapper.innerHTML;
        }
      }

    } catch (error) {
      console.error('Error refreshing cart page:', error);
    }
  }

  /**
   * Updates the minicart
   * @returns {Promise<void>}
   */
  async refreshMinicart() {
    try {
      const response = await fetch("/?sections=cart-drawer,cart-icon-bubble");
      
      if (!response.ok) {
        throw new Error("Network response was not ok");
      }

      const parsedResponse = await response.json();
      
      if (this.cart && this.cart.renderContents) {
        this.cart.renderContents({ sections: parsedResponse });
      }
    } catch (error) {
      console.error('Error refreshing minicart:', error);
    }
  }

  /**
   * Validates if a discount code is valid
   * @param {string} discountCode - Discount code to be validated
   * @returns {Promise<boolean>} True if valid, false otherwise
   */
  async validateDiscountCode(discountCode) {
    try {
      const response = await fetch(`/cart/update.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          discount: discountCode
        })
      });

      if (!response.ok) {
        return false;
      }

      const result = await response.json();
      const isValid = result.discount_applications && result.discount_applications.length > 0;
      return isValid;

    } catch (error) {
      return false;
    }
  }
}

window.CartAPIDiscountHandler = CartAPIDiscountHandler; 